import rootScope from '@lib/rootScope';
import apiManagerProxy from '@lib/apiManagerProxy';
import CorpConfig from './config';
import type {CorpPresence} from './types';

/**
 * 主线程只采集可见性 / 关页信号：
 * - 标签可见/隐藏 → 更新本标签状态，通知 Worker 汇总同账号所有标签后上报 presence
 * - 关页 pagehide(!persisted) → 通知 Worker；仅最后一个同账号标签才 offline/停心跳
 *
 * HTTP 请求和 30s 保活心跳均由 Worker 执行。
 */

let started = false;
let lastPresence: CorpPresence | undefined;
let lastReportAt = 0;
const MIN_REPORT_INTERVAL_MS = 800;

function reportPresence(presence: CorpPresence) {
  const now = Date.now();
  if(presence === lastPresence && now - lastReportAt < MIN_REPORT_INTERVAL_MS) {
    return;
  }
  lastPresence = presence;
  lastReportAt = now;

  apiManagerProxy.updateTabState('visibility', presence);
  const managers = rootScope.managers;
  if(!managers?.apiManager) return;
  Promise.resolve(managers.apiManager.corpReportPresence(presence)).catch(() => {});
}

function notifyWorkerPageHide() {
  const managers = rootScope.managers;
  if(!managers?.apiManager) return;
  // 代理调用：Worker 检查同账号其他标签；仅最后一个标签关闭时 offline。
  Promise.resolve(managers.apiManager.corpNotifyPageHide()).catch(() => {});
}

function reportPageClosing() {
  apiManagerProxy.updateTabState('visibility', 'background');
  notifyWorkerPageHide();
  lastPresence = undefined;
}

function onVisibilityChange() {
  if(typeof document === 'undefined') return;
  if(document.visibilityState === 'visible') {
    reportPresence('foreground');
  } else {
    reportPresence('background');
  }
}

export default function initCorpPresence() {
  if(!CorpConfig.enabled || CorpConfig.isAuditMode || started) return;
  if(typeof window === 'undefined' || typeof document === 'undefined') return;
  started = true;

  document.addEventListener('visibilitychange', onVisibilityChange);

  // 关页 / 卸载：最后一个同账号标签立即离线。bfcache（persisted）只标挂起，不离线。
  // 不用 beforeunload 双报，避免与 pagehide 竞态；现代浏览器 pagehide 足够。
  window.addEventListener('pagehide', (e) => {
    if(e.persisted) {
      reportPresence('background');
    } else {
      reportPageClosing();
    }
  });

  // 冷启动：若当前可见，报一次前台
  if(document.visibilityState === 'visible') {
    // 等登录态就绪：短暂延迟，未登录则 no-op
    setTimeout(() => reportPresence('foreground'), 2000);
  }

  rootScope.addEventListener('account_logged_in', () => {
    setTimeout(() => {
      if(document.visibilityState === 'visible') {
        reportPresence('foreground');
      } else {
        reportPresence('background');
      }
    }, 1000);
  });

  console.log('[corp] presence reporter started (foreground/background/offline)');
}
